import React, { useMemo, useState, useRef } from "react";
import { getAuth } from "firebase/auth";
import { db, storage, auth } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ilceKoyler from "../json/ilce-koyler.json";
import { SEKTOR_DATA, ANA_SEKTORLER } from "../hooks/sektorData";
import { X, Plus, Globe, ChevronDown, MapPin } from "lucide-react";
import ilIlceData from "../json/il-ilceler.json";
import ulkeSehirData from "../json/ulke-sehir.json";
import ProductsSection, {
  uploadAndPrepareProducts,
} from "../components/ProductsSection";

const COUNTRIES = (() => {
  const uniqueCountries = [...new Set(ulkeSehirData.map((item) => item.ulke))];
  const others = uniqueCountries
    .filter((c) => c !== "Türkiye")
    .sort((a, b) => a.localeCompare(b, "tr"));
  return ["Türkiye", ...others];
})();

const PRIORITY_CITIES = ["İstanbul", "Sivas", "Ankara", "İzmir"];

const TURKEY_CITIES = (() => {
  const allCities = ilIlceData.map((il) => il.text);

  const priority = allCities
    .filter((city) =>
      PRIORITY_CITIES.some(
        (p) => p.toLocaleLowerCase("tr") === city.toLocaleLowerCase("tr")
      )
    )
    .sort((a, b) => {
      const aIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === a.toLocaleLowerCase("tr")
      );
      const bIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === b.toLocaleLowerCase("tr")
      );
      return aIndex - bIndex;
    });

  const others = allCities
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b, "tr"));

  return [...priority, ...others];
})();
const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

const normTR = (s = "") =>
  s
    .toString()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

/** Yardımcı: sınıf birleştirici */
const cx = (...list) => list.filter(Boolean).join(" ");

const ISLETME_TURLERI = [
  "Şahıs Şirketi",
  "Limited Şirketi",
  "Anonim Şirketi",
  "Komandit Şirketi",
  "Kolektif Şirket",
];

const MAX_ABOUT = 5000;

const steps = [
  { key: "bilgiler", label: "Bilgiler" },
  { key: "konum", label: "Konum" },
  { key: "sektor", label: "Sektörler" },
  { key: "onizleme", label: "Önizleme" },
  { key: "tamamla", label: "Tamamla" },
];

const initialForm = {
  // 1) Bilgiler
  firmaAdi: "",
  telefon: "",
  fax: "",
  gsm: "",
  email: "",
  web: "",
  sosyalMedya: "",
  logoFile: null,
  logoUrl: "",
  kurulusYili: "",
  isletmeTuru: "",
  yetkiliKisi: "",
  yetkiliPozisyon: "",
  calismaSaatleri: "",
  anahtarKelimeler: [],
  anahtarKelimeInput: "",
  hakkinda: "",
  tanitimVideo: "",

  // 2) Konum
  ulke: "Türkiye",
  sehir: "",
  ilce: "",
  acikAdres: "",
  sivasIlce: "",
  sivasKoy: "",

  // 3) Sektör
  secilenSektorler: [],
  sektorInput: "",
  altSektorler: [],
  altSektorInput: "",

  isBekleyen: true,
};

export default function FirmAddPage({ setCurrentPage }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // State güncellemesi asenkron olduğu için art arda tıklamaları ref ile kilitliyoruz
  const submitLock = useRef(false);

  const activeKey = steps[stepIndex].key;

  // ---- Doğrulamalar (hafif) ----
  const validate = (forKey = activeKey) => {
    const e = {};
  
    if (forKey === "bilgiler") {
      if (!form.firmaAdi?.trim()) e.firmaAdi = "Firma adı zorunludur.";
      if (!form.email?.trim()) e.email = "E-posta zorunludur.";
      else if (!/^\S+@\S+\.\S+$/.test(form.email))
        e.email = "Geçerli bir e-posta giriniz.";
      if (!form.gsm?.trim()) e.gsm = "GSM zorunludur.";
      if (!form.yetkiliKisi?.trim()) e.gsm = "GSM zorunludur.";
      if (!form.anahtarKelimeler?.length)
        e.anahtarKelimeler = "En az bir anahtar kelime giriniz.";
    }
  
    if (forKey === "konum") {
      if (!form.ulke?.trim()) e.ulke = "Ülke zorunludur.";
      if (!form.sehir?.trim()) e.sehir = "Şehir zorunludur.";
      if (!form.ilce?.trim()) e.ilce = "İlçe zorunludur.";
      if (!form.acikAdres?.trim()) e.acikAdres = "Açık adres zorunludur.";
      if (!form.sivasIlce?.trim()) e.sivasIlce = "Sivas ilçe zorunludur.";
      if (!form.sivasKoy?.trim()) e.sivasKoy = "Köy / mahalle zorunludur.";
    }
  
    if (forKey === "sektor") {
      if (!form.secilenSektorler.length)
        e.secilenSektorler = "En az bir sektör seçiniz.";
      if (!form.altSektorler.length)
        e.altSektorler = "En az bir alt sektör seçiniz.";
    }
  
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validate()) {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }
  };
  const prev = () => setStepIndex((i) => Math.max(i - 1, 0));

  // ---- Logo yükleme / önizleme ----
  const onSelectLogo = async (f) => {
    if (!f) return;
    const url = URL.createObjectURL(f);
    setForm((p) => ({ ...p, logoFile: f, logoUrl: url }));
  };

  const removeLogo = () => {
    if (form.logoUrl) URL.revokeObjectURL(form.logoUrl);
    setForm((p) => ({ ...p, logoFile: null, logoUrl: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  // ---- Anahtar kelime ekleme ----
  const addKeyword = () => {
    const k = form.anahtarKelimeInput.trim();
    if (!k) return;
    if (form.anahtarKelimeler.includes(k)) {
      setForm((p) => ({ ...p, anahtarKelimeInput: "" }));
      return;
    }
    setForm((p) => ({
      ...p,
      anahtarKelimeler: [...p.anahtarKelimeler, k],
      anahtarKelimeInput: "",
    }));
  };

  const removeKeyword = (k) => {
    setForm((p) => ({
      ...p,
      anahtarKelimeler: p.anahtarKelimeler.filter((x) => x !== k),
    }));
  };

  const previewPairs = useMemo(
    () => [
      { k: "Firma Adı", v: form.firmaAdi || "-" },
      { k: "Telefon", v: form.telefon || "-" },
      { k: "Faks", v: form.fax || "-" },
      { k: "GSM", v: form.gsm || "-" },
      { k: "E-posta", v: form.email || "-" },
      { k: "Web", v: form.web || "-" },
      { k: "Sosyal Medya", v: form.sosyalMedya || "-" },
      { k: "Kuruluş Yılı", v: form.kurulusYili || "-" },
      { k: "İşletme Türü", v: form.isletmeTuru || "-" },
      {
        k: "Yetkili",
        v:
          [form.yetkiliKisi, form.yetkiliPozisyon]
            .filter(Boolean)
            .join(" – ") || "-",
      },
      { k: "Çalışma Saatleri", v: form.calismaSaatleri || "-" },
      { k: "Anahtar Kelimeler", v: form.anahtarKelimeler.join(", ") || "-" },
      {
        k: "Adres",
        v:
          [form.ulke, form.sehir, form.ilce].filter(Boolean).join(" / ") || "-",
      },
      { k: "Açık Adres", v: form.acikAdres || "-" },
      { k: "Sektör", v: form.secilenSektorler.join(", ") || "-" },
      { k: "Alt Sektör", v: form.altSektorler.join(", ") || "-" },
      {
        k: "Ürünler",
        v: products.length
          ? products
              .map((p) => p.ad)
              .filter(Boolean)
              .join(", ")
          : "-",
      },
    ],
    [form]
  );

  async function uploadLogoIfAny(logoFile) {
    if (!logoFile) return null;
    const user = auth.currentUser;
    if (!user) throw new Error("Oturum bulunamadı. Lütfen giriş yapın.");

    const filename = `logo_${Date.now()}_${logoFile.name || "logo.png"}`;
    const storageRef = ref(storage, `firmalar/${user.uid}/${filename}`);
    await uploadBytes(storageRef, logoFile);
    return await getDownloadURL(storageRef);
  }

  const handleSubmit = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      const authUser = getAuth().currentUser;
      if (!authUser) {
        alert("Giriş yapmadan firma ekleyemezsiniz.");
        return;
      }

      // 1) Logo varsa Storage'a YÜKLE (kural: firmalar/{uid}/...)
      const uploadedLogoUrl =
        (await uploadLogoIfAny(form.logoFile)) || form.logoUrl || "";

      const uploadedProducts = await uploadAndPrepareProducts(products);

      // 2) Firestore'a kaydedilecek veriyi hazırlayın
      const { logoFile, anahtarKelimeInput, sektorInput, ...rest } = form;
      const firmaData = {
        ...rest,
        logoUrl: uploadedLogoUrl,
        urunler: uploadedProducts,
        owner: authUser.uid,
        isBekleyen: true,
        createdAt: serverTimestamp(),
      };

      // 3) Firestore'a KAYDET
      await addDoc(collection(db, "firmalarBekleyen"), firmaData);

      alert(
        "Firma kaydı talebi başarıyla oluşturuldu! En kısa sürede talebinize dönüş yapılacaktır."
      );
      setStepIndex(0);
      setForm(initialForm);
      setProducts([]);

      // (Opsiyonel) input ref temizliği: optional chaining kullanmayın
      if (fileRef.current) fileRef.current.value = "";

      if (setCurrentPage) setCurrentPage("home");
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      alert("Bir hata oluştu. Detay: " + err.message);
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-gray-100 py-10">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Üst Başlık */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Firma Ekle</h1>
          <p className="text-gray-600 mt-2">
            Bilgileri adım adım doldurun, önizleyin ve yayına alın.
          </p>
        </div>

        {/* Stepper */}
        <Stepper
          stepIndex={stepIndex}
          setStepIndex={setStepIndex}
          disabled={submitting}
        />

        {/* Kart */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mt-6">
          <div className="p-6 md:p-8">
            {activeKey === "bilgiler" && (
              <StepBilgiler
                form={form}
                setForm={setForm}
                errors={errors}
                fileRef={fileRef}
                onSelectLogo={onSelectLogo}
                removeLogo={removeLogo}
                addKeyword={addKeyword}
                removeKeyword={removeKeyword}
              />
            )}

            {activeKey === "konum" && (
              <StepKonum form={form} setForm={setForm} errors={errors} />
            )}

            {activeKey === "sektor" && (
              <StepSektor
                form={form}
                setForm={setForm}
                errors={errors}
                products={products}
                setProducts={setProducts}
              />
            )}

            {activeKey === "onizleme" && (
              <StepOnizleme
                form={form}
                previewPairs={previewPairs}
                products={products}
              />
            )}

            {activeKey === "tamamla" && (
              <StepTamamla
                handleSubmit={handleSubmit}
                submitting={submitting}
              />
            )}
          </div>

          {/* Alt Butonlar */}
          <div className="px-6 md:px-8 py-4 border-t flex items-center justify-between bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={prev}
              disabled={stepIndex === 0 || submitting}
              className={cx(
                "px-4 py-2 rounded-lg font-medium transition flex items-center",
                stepIndex === 0 || submitting
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border hover:bg-gray-50 text-gray-700"
              )}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Geri
            </button>

            <div className="text-sm text-gray-500 hidden md:block">
              Adım {stepIndex + 1} / {steps.length}
            </div>

            {activeKey !== "tamamla" ? (
              <button
                type="button"
                onClick={next}
                className="bg-amber-400 hover:bg-amber-500 text-white font-semibold px-5 py-2 rounded-lg transition flex items-center"
              >
                Devam Et
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
function ASelect({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition appearance-none bg-white
        disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function AInput({
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
  min,
  max,
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition"
    />
  );
}
function AdminInput({ label, error, children, span2 }) {
  return (
    <div className={cx(span2 && "md:col-span-2")}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}
/* --------------------- Stepper --------------------- */
function Stepper({ stepIndex, setStepIndex, disabled }) {
  return (
    <div className="flex items-center justify-center">
      {steps.map((s, i) => {
        const active = i === stepIndex;
        const done = i < stepIndex;
        return (
          <React.Fragment key={s.key}>
            <button
              type="button"
              onClick={() => setStepIndex(i)}
              disabled={disabled}
              className={cx(
                "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition",
                done && "bg-emerald-500 text-white",
                active && !done && "bg-amber-400 text-white",
                !active && !done && "bg-gray-200 text-gray-700"
              )}
              title={s.label}
            >
              {done ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                i + 1
              )}
            </button>
            {i < steps.length - 1 && (
              <div
                className={cx(
                  "w-16 h-1 mx-2 rounded",
                  i < stepIndex ? "bg-emerald-400" : "bg-gray-200"
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* --------------------- Step 1: Bilgiler --------------------- */
function StepBilgiler({
  form,
  setForm,
  errors,
  fileRef,
  onSelectLogo,
  removeLogo,
  addKeyword,
  removeKeyword,
}) {
  const charLeft = MAX_ABOUT - form.hakkinda.length;

  return (
    <div className="space-y-8">
      {/* Logo + Firma Adı */}
      <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-6 items-start">
        {/* Logo */}
        <div>
          <div className="w-32 h-32 bg-gray-100 border border-dashed border-gray-300 rounded-xl flex items-center justify-center relative overflow-hidden">
            {form.logoUrl ? (
              <img
                src={form.logoUrl}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-gray-400 text-sm">Logo Yükle</span>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={(e) =>
                onSelectLogo(e.target.files && e.target.files[0])
              }
            />
          </div>
          {form.logoUrl && (
            <button
              type="button"
              onClick={removeLogo}
              className="mt-2 text-xs text-red-600 hover:underline"
            >
              Logoyu Kaldır
            </button>
          )}
        </div>

        {/* Firma Adı */}
        <div>
          <Label>Firma Adı *</Label>
          <Input
            value={form.firmaAdi}
            onChange={(e) =>
              setForm((p) => ({ ...p, firmaAdi: e.target.value }))
            }
            placeholder="Örnek Teknoloji A.Ş."
            error={errors.firmaAdi}
          />
        </div>
      </div>

      {/* İletişim satırı */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Firma Telefon No.">
          <Input
            value={form.telefon}
            onChange={(e) =>
              setForm((p) => ({ ...p, telefon: e.target.value }))
            }
            placeholder="0 (212) 000 00 00"
          />
        </Field>
        <Field label="Firma Faks No.">
          <Input
            value={form.fax}
            onChange={(e) => setForm((p) => ({ ...p, fax: e.target.value }))}
            placeholder="0 (212) 000 00 00"
          />
        </Field>
        <Field label="Firma GSM No. *" error={errors.gsm}>
          <Input
            value={form.gsm}
            onChange={(e) => setForm((p) => ({ ...p, gsm: e.target.value }))}
            placeholder="05XX XXX XX XX"
          />
        </Field>
      </div>

      {/* E-posta / Web / LinkedIn */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Firma E-posta Adresi *" error={errors.email}>
          <Input
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            placeholder="ornek@email.com"
          />
        </Field>
        <Field label="Firma Web Sitesi" >
          <Input
            value={form.web}
            onChange={(e) => setForm((p) => ({ ...p, web: e.target.value }))}
            placeholder="https://example.com"
          />
        </Field>
        <Field label="Sosyal Medya URL">
          <Input
            value={form.sosyalMedya}
            onChange={(e) =>
              setForm((p) => ({ ...p, sosyalMedya: e.target.value }))
            }
            placeholder="https://instagram.com/company"
          />
        </Field>
      </div>

      {/* Kuruluş / İşletme Türü */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Kuruluş Yılı">
          <Input
            type="number"
            min="1800"
            max={new Date().getFullYear()}
            value={form.kurulusYili}
            onChange={(e) =>
              setForm((p) => ({ ...p, kurulusYili: e.target.value }))
            }
            placeholder="2020"
          />
        </Field>
        <Field label="İşletme Türü">
          <Select
            value={form.isletmeTuru}
            onChange={(e) =>
              setForm((p) => ({ ...p, isletmeTuru: e.target.value }))
            }
          >
            <option value="">Seçiniz</option>
            {ISLETME_TURLERI.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Yetkili Kişi *" error={errors.yetkiliKisi}>
            <Input
              value={form.yetkiliKisi}
              onChange={(e) =>
                setForm((p) => ({ ...p, yetkiliKisi: e.target.value }))
              }
              placeholder="Ad Soyad"
            />
          </Field>
          <Field label="Yetkili Pozisyon">
            <Input
              value={form.yetkiliPozisyon}
              onChange={(e) =>
                setForm((p) => ({ ...p, yetkiliPozisyon: e.target.value }))
              }
              placeholder="Genel Müdür"
            />
          </Field>
        </div>
      </div>

      {/* Çalışma Saatleri */}
      <Field label="Çalışma Saatleri">
        <Input
          value={form.calismaSaatleri}
          onChange={(e) =>
            setForm((p) => ({ ...p, calismaSaatleri: e.target.value }))
          }
          placeholder="Hafta içi 09:00 - 18:00, Cumartesi 10:00 - 16:00"
        />
      </Field>

      {/* Video URL */}
      <Field label="Tanıtım Filmi (Opsiyonel)">
        <p className="mb-1.5 text-xs text-slate-500">
          💡 YouTube'da firmanıza ait tanıtım videonuz varsa bağlantısını buraya
          yapıştırabilirsiniz.
        </p>
        <Input
          value={form.tanitimVideo}
          onChange={(e) =>
            setForm((p) => ({ ...p, tanitimVideo: e.target.value }))
          }
          placeholder="https://www.youtube.com/watch?v=..."
        />
      </Field>

      {/* Anahtar Kelimeler */}
      <div>
        <Label>Anahtar Kelimeler *</Label>
        <div className="flex gap-2">
          <input
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
            value={form.anahtarKelimeInput}
            onChange={(e) =>
              setForm((p) => ({ ...p, anahtarKelimeInput: e.target.value }))
            }
            onKeyDown={(e) =>
              e.key === "Enter" ? (e.preventDefault(), null) : null
            }
            placeholder="Hangi kelimeler ile bulunmak istiyorsanız ekleyiniz . Örn: seramik ustası"
          />
          <button
            type="button"
            onClick={() => {
              addKeyword();
            }}
            className="px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-500 text-white font-medium"
          >
            Ekle
          </button>
          {errors.anahtarKelimeler && <ErrorText>{errors.anahtarKelimeler}</ErrorText>}
        </div>
        
        {!!form.anahtarKelimeler.length && (
          <div className="flex flex-wrap gap-2 mt-3">
            {form.anahtarKelimeler.map((k) => (
              <span
                key={k}
                className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-sm border"
              >
                {k}
                <button
                  type="button"
                  onClick={() => removeKeyword(k)}
                  className="ml-2 text-gray-400 hover:text-red-500"
                  title="Kaldır"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Hakkında */}
      <div>
        <Label>Firma Hakkında* (max {MAX_ABOUT}) </Label>
        <textarea
          value={form.hakkinda}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              hakkinda: e.target.value.slice(0, MAX_ABOUT),
            }))
          }
          rows={6}
          placeholder="Firmanızı detaylı şekilde anlatın..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <div
          className={cx(
            "text-right text-xs mt-1",
            charLeft < 200 && "text-amber-600"
          )}
        >
          {charLeft} karakter kaldı
        </div>
        {errors.hakkinda && <ErrorText>{errors.hakkinda}</ErrorText>}
      </div>
    </div>
  );
}

/* --------------------- Step 2: Konum --------------------- */
function StepKonum({ form, setForm, errors }) {
  const [country, setCountry] = React.useState("Türkiye");
  const [city, setCity] = React.useState("");
  const [cityDistrict, setCityDistrict] = React.useState("");

  // Türkiye seçili mi?
  const isTurkiye = country === "Türkiye";

  // Seçilen ile göre ilçeleri al (il-ilceler.json'dan) - SADECE TÜRKİYE İÇİN
  const cityDistricts = React.useMemo(() => {
    if (!city || !isTurkiye) return [];
    const ilData = ilIlceData.find((il) => normTR(il.text) === normTR(city));
    if (!ilData) return [];
    return ilData.districts
      .map((d) => d.text)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [city, isTurkiye]);

  const ilceler = useMemo(
    () => [...new Set(ilceKoyler.map((x) => x.ilce))].sort(),
    []
  );

  const koyler = useMemo(() => {
    if (!form.sivasIlce) return [];
    const list = ilceKoyler
      .filter((x) => x.ilce === form.sivasIlce)
      .map((x) => x.koy);
    const merkez = list.filter((k) =>
      k.toLocaleLowerCase("tr").includes("merkez")
    );
    const diger = list
      .filter((k) => !k.toLocaleLowerCase("tr").includes("merkez"))
      .sort((a, b) => a.localeCompare(b, "tr"));
    return [...merkez, ...diger];
  }, [form.sivasIlce]);

  React.useEffect(() => {
    setCity("");
    setCityDistrict("");
  }, [country]);

  // İl değiştiğinde ilçeyi sıfırla
  React.useEffect(() => {
    setCityDistrict("");
  }, [city]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Ülke *" error={errors.ulke}>
          {/* Ülke Dropdown */}
          <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
            <Globe className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
            <div className="min-w-0 w-full relative">
              <select
                value={form.ulke}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setForm((p) => ({
                    ...p,
                    ulke: e.target.value,
                    sehir: "",
                    ilce: "",
                  }));
                }}
                className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer"
              >
                {COUNTRIES.map((ulke) => (
                  <option key={ulke} value={ulke}>
                    {ulke}
                  </option>
                ))}
              </select>
            </div>
            <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
          </div>
        </Field>
        <Field label="Şehir *" error={errors.sehir}>
          {/* İl Dropdown (Sadece Türkiye için aktif) */}
          <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
            <MapPin className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
            <div className="min-w-0 w-full relative">
              {!city && (
                <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                  {isTurkiye ? "İl seçiniz..." : "Sadece Türkiye"}
                </span>
              )}
              <select
                value={form.sehir}
                onChange={(e) => {
                  setCity(e.target.value);
                  setForm((p) => ({ ...p, sehir: e.target.value, ilce: "" }));
                }}
                disabled={!isTurkiye}
                className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
              >
                <option value=""></option>
                {isTurkiye &&
                  TURKEY_CITIES.map((il) => (
                    <option key={il} value={il}>
                      {il}
                    </option>
                  ))}
              </select>
            </div>
            <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
          </div>
        </Field>
        <Field label="İlçe *" error={errors.ilce}>
          {/* İl İçin İlçe Dropdown (Sadece Türkiye + il seçiliyse) */}
          <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
            <ChevronDown className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
            <div className="min-w-0 w-full relative">
              {!cityDistrict && (
                <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                  {!isTurkiye
                    ? "Sadece Türkiye"
                    : city
                    ? "İlçe seçiniz..."
                    : "Önce il seçiniz..."}
                </span>
              )}
              <select
                value={form.ilce}
                onChange={(e) => {
                  setCityDistrict(e.target.value);
                  setForm((p) => ({ ...p, ilce: e.target.value }));
                }}
                disabled={!city || !isTurkiye}
                className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
              >
                <option value=""></option>
                {cityDistricts.map((ilce) => (
                  <option key={ilce} value={ilce}>
                    {toTitleTR(ilce)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Field>
      </div>
      <Field label="Açık Adres *" error={errors.acikAdres}>
        <textarea
          rows={4}
          placeholder="Mahalle, cadde/sokak, bina, kapı no..."
          value={form.acikAdres}
          onChange={(e) =>
            setForm((p) => ({ ...p, acikAdres: e.target.value }))
          }
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </Field>
      <div className="md:col-span-4">
        <p className="text-sm font-medium text-slate-700 mb-3">
          Sivasın hangi ilçe / köyündensiniz?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AdminInput label="İlçe *" error={errors.sivasIlce}>
            <ASelect
              value={form.sivasIlce}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  sivasIlce: e.target.value,
                  sivasKoy: "",
                }))
              }
            >
              <option value="">İlçe seçiniz…</option>
              {ilceler.map((il) => (
                <option key={il} value={il}>
                  {il}
                </option>
              ))}
            </ASelect>
          </AdminInput>

          <AdminInput label="Köy / Mahalle *" error={errors.sivasKoy}>
            <ASelect
              value={form.sivasKoy}
              onChange={(e) =>
                setForm((p) => ({ ...p, sivasKoy: e.target.value }))
              }
              disabled={!form.sivasIlce}
            >
              <option value="">
                {form.sivasIlce ? "Köy seçiniz…" : "Önce ilçe seçiniz"}
              </option>
              {koyler.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </ASelect>
          </AdminInput>
        </div>
      </div>

      
    </div>
  );
}

/* --------------------- Step 3: Sektör --------------------- */
function StepSektor({ form, setForm, errors, products, setProducts }) {
  const secilenSektorler = form.secilenSektorler || [];
  const altSektorler = form.altSektorler || [];

  const toSentenceCase = (s = "") =>
    s.charAt(0).toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr");
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const addSektor = (s) => {
    const v = s.trim();
    if (!v || secilenSektorler.includes(v)) return set({ sektorInput: "" });
    set({ secilenSektorler: [...secilenSektorler, v], sektorInput: "" });
  };

  const addAltSektor = (s) => {
    const v = s.trim();
    if (!v || altSektorler.includes(v)) return set({ altSektorInput: "" });
    set({ altSektorler: [...altSektorler, v], altSektorInput: "" });
  };

  return (
    <div className="space-y-6">
      <div>
        <Label>Sektör Ekle *</Label>
        {/* ── Sektörler ── */}
        <SectionHeading>Sektörler *</SectionHeading>

        <div>
          <div className="flex gap-2">
            <AInput
              value={form.sektorInput}
              onChange={(e) => set({ sektorInput: e.target.value })}
              placeholder="Sektör adı yazın veya aşağıdan seçin…"
            />
            <button
              type="button"
              onClick={() => addSektor(form.sektorInput)}
              className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Ekle
            </button>
          </div>
          {errors.secilenSektorler && (
            <p className="mt-1 text-xs text-red-500">
              {errors.secilenSektorler}
            </p>
          )}

          {/* Öneriler */}
          <ASelect
            value=""
            onChange={(e) => {
              if (e.target.value) addSektor(e.target.value);
            }}
          >
            <option value="">Ana sektör seçin…</option>
            {ANA_SEKTORLER.map((s) => (
              <option key={s} value={s}>
                {toSentenceCase(s)}
              </option>
            ))}
          </ASelect>
          {/* Seçilenler */}
          {form.secilenSektorler.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {form.secilenSektorler.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                >
                  {s}
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        secilenSektorler: form.secilenSektorler.filter(
                          (x) => x !== s
                        ),
                      })
                    }
                    className="text-amber-600 hover:text-red-600 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Alt Sektörler ── */}
        <SectionHeading>Alt Sektörler *</SectionHeading>

        <div>
          <div className="flex gap-2">
            <AInput
              value={form.altSektorInput}
              onChange={(e) => set({ altSektorInput: e.target.value })}
              placeholder="Listede yoksa yazın…"
            />
            <button
              type="button"
              onClick={() => addAltSektor(form.altSektorInput)}
              className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Ekle
            </button>
          </div>
          {errors.altSektorler && (
            <p className="mt-1 text-xs text-red-500">{errors.altSektorler}</p>
          )}

          {/* Öneriler */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(() => {
              const altSecenekler =
                form.secilenSektorler.length > 0
                  ? [
                      ...new Set(
                        form.secilenSektorler.flatMap(
                          (s) => SEKTOR_DATA[s] || []
                        )
                      ),
                    ]
                  : [];
              return (
                <ASelect
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addAltSektor(e.target.value);
                  }}
                  disabled={altSecenekler.length === 0}
                >
                  <option value="">
                    {form.secilenSektorler.length > 0
                      ? "Alt sektör seçin…"
                      : "Önce ana sektör seçin"}
                  </option>
                  {altSecenekler.map((s) => (
                    <option key={s} value={s}>
                      {toSentenceCase(s)}
                    </option>
                  ))}
                </ASelect>
              );
            })()}
          </div>

          {/* Seçilenler */}
          {form.altSektorler.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {form.altSektorler.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                >
                  {s}
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        altSektorler: form.altSektorler.filter((x) => x !== s),
                      })
                    }
                    className="text-amber-600 hover:text-red-600 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <ProductsSection products={products} setProducts={setProducts} />
    </div>
  );
}

/* --------------------- Step 4: Önizleme --------------------- */
function StepOnizleme({ form, previewPairs, products }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
      {/* Sol: Logo */}
      <div>
        <div className="w-48 h-48 bg-gray-100 border border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden mx-auto">
          {form.logoUrl ? (
            <img src={form.logoUrl} alt="Logo" className="w-full h-full object-contain" />
          ) : (
            <span className="text-gray-400 text-sm">Logo yok</span>
          )}
        </div>
      </div>

      {/* Sağ: Bilgiler */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-800">
          {form.firmaAdi || "Firma Adı (girilmedi)"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {previewPairs.map((row) => (
            <div key={row.k} className="p-3 bg-gray-50 rounded-lg border">
              <div className="text-xs text-gray-500">{row.k}</div>
              <div className="text-sm text-gray-800">{row.v}</div>
            </div>
          ))}
        </div>

        {form.hakkinda && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Hakkında</div>
            <div className="p-4 bg-white border rounded-lg prose prose-sm max-w-none">
              {form.hakkinda}
            </div>
          </div>
        )}

        {/* Ürünler — sağ kolonda, diğer içeriklerin altında */}
        {products.length > 0 && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Ürünler & Hizmetler ({products.length})
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {products.map((p, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded-lg border flex items-center gap-2">
                  {(p.imagePreviewUrl || p.imageUrl) && (
                    <img
                      src={p.imagePreviewUrl || p.imageUrl}
                      alt={p.ad}
                      className="w-10 h-10 object-cover rounded shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-700 line-clamp-1">
                      {p.ad || "İsimsiz"}
                    </p>
                    {p.aciklama && (
                      <p className="text-[10px] text-gray-400 line-clamp-1">{p.aciklama}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------- Step 5: Tamamla --------------------- */
function StepTamamla({ handleSubmit, submitting }) {
  return (
    <div className="text-center py-10">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
        <svg
          className="w-9 h-9"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-gray-800">Son adım!</h3>
      <p className="text-gray-600 mt-2">
        Bilgilerinizi kontrol ettiyseniz kaydı tamamlayabilirsiniz.
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-lg transition inline-flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Kaydediliyor…" : "Kaydı Tamamla"}
          <svg
            className="w-5 h-5 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* --------------------- UI Parçaları --------------------- */
function Field({ label, error, children }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="mb-2 text-sm font-medium text-gray-700">{children}</div>
  );
}
function ErrorText({ children }) {
  return <div className="mt-1 text-xs text-red-600">{children}</div>;
}
function Input(props) {
  return (
    <input
      {...props}
      className={cx(
        "w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400",
        "border-gray-300",
        props.className
      )}
    />
  );
}
function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
    >
      {children}
    </select>
  );
}
